import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Drawer,
  Empty,
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  Modal,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Grid,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useNavigate } from "react-router-dom";
import {
  getAgentBillingExemptionSalesAudit,
  updateTeamAgencyName,
  createTeamSeatUser,
  updateTeamHandlingFeeSplit,
  updateMemberHandlingFeeSplit,
} from "../../services/agentAccountApi";
import { useAgentAccountOverview } from "../../contexts/AgentAccountOverviewContext";
import {
  openAgentPaymentMethodSetup,
  resolveAgentBillingSetupRedirectUrl,
} from "../../services/agentBillingStatusApi";
import { useAgentBillingStatus } from "../../contexts/AgentBillingStatusContext";
import { FeatureGuardCard, useAgentEntitlements } from "../../features/agentEntitlements";
import { showAppToastError, showAppToastSuccess } from "../../utils/appToast/showAppToast";
import {
  agentPackageLabelHe,
  normalizeAgentPackageCode,
  teamMemberPackageCodeForDisplay,
} from "../../utils/agentPackageDisplay";
import {
  resolveAgentDisplayedPackage,
  shouldShowUpgradeCta,
} from "../../utils/resolveAgentDisplayedPackage";
import { navigateAgentPackageUpgrade } from "../../utils/agentUpgradeNavigation";
import { getRegistrationPlanDefinition } from "../../utils/agentRegistrationPlans";
import {
  resolvePackageSubscriptionChargeDisplay,
  resolvePackageSubscriptionGrossAmount,
} from "../../utils/resolvePackagePriceWithVat";
import {
  formatMonthlyGrossAmountIls,
  resolveSubscriptionBillingSummary,
} from "../../utils/subscriptionBillingPolicySsot";
import { ordersDashboardCategoryPath } from "../../utils/ordersDashboardRoutes";
import BillingExemptionCard from "../../Components/Agent/BillingExemptionCard/BillingExemptionCard";
import ReferralInviteCard from "../../Components/Agent/ReferralInviteCard/ReferralInviteCard";
import HandlingFeeSplitSlider from "./HandlingFeeSplitSlider";
import "./AgentAccountOverview.scss";

dayjs.extend(utc);

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

/** תווית סטטוס מנוי בעברית — לא מציגים מפתחות גולמיים מהשרת */
function subscriptionStatusLabelHe(status) {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return "—";
  const map = {
    pending_manual_approval: "ממתין לאישור",
    approved_trial_active: "פעיל",
    trial_active: "פעיל",
    active_paid: "פעיל",
    paused: "מוקפא",
    past_due: "חוב",
    legacy_unbilled: "נדרשת הסדרת אמצעי תשלום",
  };
  return map[s] || "—";
}

function subscriptionStatusTagHe(status) {
  const s = String(status || "").trim().toLowerCase();
  const label = subscriptionStatusLabelHe(status);
  if (!s) return <Tag>—</Tag>;
  if (s === "pending_manual_approval") return <Tag color="gold">{label}</Tag>;
  if (s === "past_due") return <Tag color="error">{label}</Tag>;
  if (s === "paused") return <Tag color="magenta">{label}</Tag>;
  if (s === "legacy_unbilled") return <Tag color="orange">{label}</Tag>;
  if (s === "trial_active" || s === "active_paid" || s === "approved_trial_active") {
    return <Tag color="success">{label}</Tag>;
  }
  return <Tag>{label}</Tag>;
}

function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const cur = String(currency || "ILS").toUpperCase();
  try {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: cur === "ILS" ? "ILS" : cur,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${amount} ${cur}`;
  }
}

/** תצוגת מנוי: תוכנית + ברוטו + חיוב הבא + הוראת קבע (SSOT מדיניות חיוב). */
function SubscriptionBillingSummaryBlock({ summary, titleLevel = 3 }) {
  if (!summary) return null;
  const levelProps = titleLevel === 2 ? { level: 2 } : { level: 3 };
  const gross =
    summary.monthlyGrossAmount != null
      ? formatMonthlyGrossAmountIls(summary.monthlyGrossAmount)
      : "—";

  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {summary.packageCode ? (
        <Text strong style={{ fontSize: 16, letterSpacing: 0.5 }}>
          {agentPackageLabelHe(summary.packageCode)}
        </Text>
      ) : null}
      <Title {...levelProps} style={{ margin: 0 }}>
        {gross}
      </Title>
      {summary.nextBillingDateDisplay ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          חיוב הבא: {summary.nextBillingDateDisplay}
        </Text>
      ) : null}
      <Text type="secondary" style={{ fontSize: 13 }}>
        הוראת קבע: {summary.standingOrderStatusHe || "—"}
      </Text>
    </Space>
  );
}

/** סכום מנוי (נטו/ברוטו) — לכרטיס «חיוב צפוי». */
function SubscriptionChargeAmountBlock({ netAmount, titleLevel = 3 }) {
  const display = resolvePackageSubscriptionChargeDisplay(netAmount);
  const levelProps = titleLevel === 2 ? { level: 2 } : { level: 3 };

  return (
    <>
      <Title {...levelProps} style={{ margin: 0 }}>
        {display.netFormatted}
      </Title>
      {display.grossFormatted !== "—" ? (
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
          {display.grossFormatted} {display.vatIncludedLabel}
        </Text>
      ) : null}
    </>
  );
}

/** תאריך/שעה מקומיים ברורים (מחרוזת ISO מהשרת, לרוב עם Z). */
function formatSubscriptionInstant(iso) {
  if (!iso) return null;
  const d = dayjs.utc(iso);
  if (!d.isValid()) return String(iso);
  const dt = d.local().toDate();
  try {
    return new Intl.DateTimeFormat("he-IL", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(dt);
  } catch {
    return d.local().format("DD/MM/YYYY HH:mm");
  }
}

function subscriptionHasRecordedCharge(sub) {
  if (sub?.has_recorded_subscription_charge != null) {
    return Boolean(sub.has_recorded_subscription_charge);
  }
  return Boolean(sub?.last_charge_at);
}

/** תאימות כשאין בלוק billing (למשל ללא billing_access) — עדיין משתמש במחיר מהמנוי / display_billing. */
function resolveBillingPayload(raw) {
  const sub = raw?.subscription;
  const disp = raw?.display_billing;
  const b = raw?.billing;
  const fallbackMonthly = disp?.monthly_amount ?? sub?.monthly_amount;
  if (b && typeof b === "object") {
    return b;
  }
  return {
    base_amount: fallbackMonthly,
    team_credit: 0,
    current_charge_amount: fallbackMonthly,
    currency: disp?.currency ?? sub?.currency ?? "ILS",
    next_charge_at: sub?.next_charge_at,
  };
}

/** מחיר חבילה נטו מהשרת — לתצוגה ב«חיוב נוכחי». */
function resolveCurrentPackageNetAmount(resolvedPackage) {
  const amount =
    resolvedPackage?.monthlyAmount ?? resolvedPackage?.monthly_amount;
  if (amount != null && !Number.isNaN(Number(amount))) {
    return Number(amount);
  }
  return null;
}

function packageCardHighlights(packageCode, seatBilling) {
  if (packageCode === "TRIAL") {
    return ["60 יום ללא עלות", "ללא כרטיס אשראי", "בחירת חבילה בתשלום בסוף הניסיון"];
  }
  if (packageCode === "AGENCY") {
    const included = Number(seatBilling?.includedUsers) || 4;
    return [
      `עד ${included} משתמשים כלולים`,
      "ניהול צוות והרשאות",
      "כל יכולות PRO",
    ];
  }
  if (packageCode === "PRO") {
    return ["אתר סוכן, לידים ו-SEO", "הצעות מחיר חכמות", "תמיכה בעדיפות"];
  }
  return ["מלונות וחבילות נופש", "סליקה ופיצול תשלומים", "תמיכה במייל"];
}

/** @deprecated — השתמשו ב-resolveCurrentPackageNetAmount + SubscriptionChargeAmountBlock */
function resolveCurrentPackageChargeAmount(resolvedPackage) {
  const net = resolveCurrentPackageNetAmount(resolvedPackage);
  if (net == null) return null;
  return resolvePackageSubscriptionGrossAmount(net);
}

export default function AgentAccountOverview() {
  const navigate = useNavigate();
  const {
    data: overviewFromCtx,
    loading: overviewLoading,
    error: overviewError,
    refresh: refreshOverview,
  } = useAgentAccountOverview();
  const [data, setData] = useState(overviewFromCtx);
  const loading = overviewLoading;
  const error = overviewError;
  const load = refreshOverview;
  const [salesAuditOpen, setSalesAuditOpen] = useState(false);
  const [salesAuditLoading, setSalesAuditLoading] = useState(false);
  const [salesAuditError, setSalesAuditError] = useState(null);
  const [salesAuditData, setSalesAuditData] = useState(null);
  const [salesAuditQuery, setSalesAuditQuery] = useState("");
  const screens = useBreakpoint();
  // billing status נטען ברמת AgentDashboardChrome (provider משותף) —
  // כאן רק צורכים אותו כדי למנוע fetch כפול ו-banner כפול.
  const { billingStatus, showBillingMigrationGate, reload: reloadBillingStatus } =
    useAgentBillingStatus();
  const { canUseTeamManagement } = useAgentEntitlements();
  const [paymentSetupLoading, setPaymentSetupLoading] = useState(false);
  const [paymentSetupError, setPaymentSetupError] = useState(null);
  const [agencyNameDraft, setAgencyNameDraft] = useState("");
  const [agencyNameSaving, setAgencyNameSaving] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserSubmitting, setCreateUserSubmitting] = useState(false);
  const [createUserForm] = Form.useForm();
  const [defaultSplitSaving, setDefaultSplitSaving] = useState(false);
  const [memberSplitSavingId, setMemberSplitSavingId] = useState(null);
  const prevPaymentCoverageRef = useRef(undefined);

  useEffect(() => {
    if (!overviewFromCtx) return;
    setData(overviewFromCtx);
    const existingName =
      overviewFromCtx?.team?.agency_name || overviewFromCtx?.agent?.team_agency_name || "";
    setAgencyNameDraft(String(existingName || ""));
  }, [overviewFromCtx]);

  const subscription = data?.subscription;
  const displayBilling = data?.display_billing;
  const billing = data ? resolveBillingPayload(data) : null;
  const currentPackageNetAmount = data
    ? resolveCurrentPackageNetAmount(data?.resolvedPackage)
    : null;
  const currentPackageChargeAmount = data
    ? resolveCurrentPackageChargeAmount(data?.resolvedPackage)
    : null;
  const billCurrency = String(
    billing?.currency ?? displayBilling?.currency ?? subscription?.currency ?? "ILS",
  ).toUpperCase();

  const hasPaymentMethod =
    displayBilling?.has_payment_method === true ||
    billingStatus?.hasActivePaymentCoverage === true;

  useEffect(() => {
    const cur = billingStatus?.hasActivePaymentCoverage;
    if (prevPaymentCoverageRef.current === false && cur === true) {
      load();
    }
    prevPaymentCoverageRef.current = cur;
  }, [billingStatus?.hasActivePaymentCoverage, load]);

  const scheduledNextIso = billing?.next_charge_at ?? subscription?.next_charge_at;
  const billingExemptionPayload = data?.billing_exemption ?? billingStatus?.billingExemption ?? null;
  const billingExemptionSource = data?.billing_exemption
    ? "account_overview.billing_exemption"
    : billingStatus?.billingExemption
      ? "billing_status.billingExemption"
      : "missing";
  const team = data?.team;
  const teamAgencyName = String(
    team?.agency_name || data?.agent?.team_agency_name || ""
  ).trim();
  const saveAgencyName = useCallback(async () => {
    setAgencyNameSaving(true);
    try {
      const res = await updateTeamAgencyName(agencyNameDraft);
      if (res?.ok === false) {
        showAppToastError(res?.message || "שמירת שם הסוכנות נכשלה", {
          source: "team-agency-name",
        });
        return;
      }
      const saved = res?.agencyName || "";
      setAgencyNameDraft(String(saved || ""));
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          team: {
            ...(prev.team || {}),
            agency_name: saved || null,
          },
          agent: {
            ...(prev.agent || {}),
            team_agency_name: saved || null,
          },
        };
      });
      showAppToastSuccess("שם הסוכנות נשמר", { source: "team-agency-name" });
      try {
        window.dispatchEvent(
          new CustomEvent("teamAgencyNameChanged", { detail: { agencyName: saved } })
        );
      } catch {
        /* ignore */
      }
    } catch (e) {
      showAppToastError(
        e?.response?.data?.message || e?.message || "שמירת שם הסוכנות נכשלה",
        { source: "team-agency-name" }
      );
    } finally {
      setAgencyNameSaving(false);
    }
  }, [agencyNameDraft]);

  const defaultSplitPercent = Number(
    team?.handling_fee_split?.agent_percent ?? team?.handling_fee_split?.agentPercent ?? 100
  );
  const seatBilling = team?.seat_billing || team?.seatBilling || null;

  const persistDefaultSplit = useCallback(
    async (agentPercent) => {
      setDefaultSplitSaving(true);
      try {
        const res = await updateTeamHandlingFeeSplit(agentPercent);
        if (res?.ok === false) {
          showAppToastError(res?.message || "שמירת חלוקת דמי הטיפול נכשלה", {
            source: "team-handling-fee-split",
          });
          return;
        }
        const saved = Number(
          res?.handlingFeeSplit?.agentPercent ?? agentPercent
        );
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            team: {
              ...(prev.team || {}),
              handling_fee_split: {
                agent_percent: saved,
                team_lead_percent: Math.round((100 - saved) * 100) / 100,
              },
            },
          };
        });
        showAppToastSuccess("חלוקת דמי הטיפול נשמרה", {
          source: "team-handling-fee-split",
        });
      } catch (e) {
        showAppToastError(
          e?.response?.data?.message || e?.message || "שמירת חלוקת דמי הטיפול נכשלה",
          { source: "team-handling-fee-split" }
        );
      } finally {
        setDefaultSplitSaving(false);
      }
    },
    []
  );

  const persistMemberSplit = useCallback(async (memberId, agentPercent) => {
    setMemberSplitSavingId(memberId);
    try {
      const res = await updateMemberHandlingFeeSplit(memberId, agentPercent);
      if (res?.ok === false) {
        showAppToastError(res?.message || "שמירת החלוקה לסוכן נכשלה", {
          source: "member-handling-fee-split",
        });
        return;
      }
      const saved = Number(res?.handlingFeeSplit?.agentPercent ?? agentPercent);
      setData((prev) => {
        if (!prev) return prev;
        const list = Array.isArray(prev.team?.members) ? prev.team.members : [];
        return {
          ...prev,
          team: {
            ...(prev.team || {}),
            members: list.map((m) =>
              m.id === memberId ? { ...m, handling_fee_agent_percent: saved } : m
            ),
          },
        };
      });
    } catch (e) {
      showAppToastError(
        e?.response?.data?.message || e?.message || "שמירת החלוקה לסוכן נכשלה",
        { source: "member-handling-fee-split" }
      );
    } finally {
      setMemberSplitSavingId(null);
    }
  }, []);

  const submitCreateUser = useCallback(async () => {
    try {
      const values = await createUserForm.validateFields();
      setCreateUserSubmitting(true);
      const res = await createTeamSeatUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || "",
        password: values.password,
      });
      if (res?.ok === false) {
        showAppToastError(res?.message || "יצירת המשתמש נכשלה", {
          source: "team-create-user",
        });
        return;
      }
      showAppToastSuccess("המשתמש נוצר וניתן להתחבר עם האימייל והסיסמה שהוגדרו", {
        source: "team-create-user",
      });
      setCreateUserOpen(false);
      createUserForm.resetFields();
      await load();
    } catch (e) {
      if (e?.errorFields) return;
      showAppToastError(
        e?.response?.data?.message || e?.message || "יצירת המשתמש נכשלה",
        { source: "team-create-user" }
      );
    } finally {
      setCreateUserSubmitting(false);
    }
  }, [createUserForm, load]);
  const auditMonth = useMemo(
    () => String(billingExemptionPayload?.sales_month || dayjs.utc().format("YYYY-MM")),
    [billingExemptionPayload?.sales_month]
  );
  const resolvedDisplayedPackage = useMemo(
    () =>
      resolveAgentDisplayedPackage({
        resolvedPackage: data?.resolvedPackage,
      }),
    [data?.resolvedPackage]
  );
  const isAgencyTeamDisplay = Boolean(
    team?.is_team_lead ||
      resolvedDisplayedPackage.packageCode === "AGENCY" ||
      (!team?.is_team_lead &&
        normalizeAgentPackageCode(team?.lead_package_code) === "AGENCY")
  );
  const packageCode = isAgencyTeamDisplay
    ? "AGENCY"
    : resolvedDisplayedPackage.packageCode;
  const packageNameHe = isAgencyTeamDisplay
    ? "סוכנות"
    : resolvedDisplayedPackage.packageNameHe;
  const resolvedPackage = data?.resolvedPackage;
  const pendingPackageCode =
    subscription?.pendingPackageCode || subscription?.pending_package_code || null;
  const requiresNewStandingOrder = Boolean(
    subscription?.requiresNewStandingOrder ||
      (pendingPackageCode &&
        ["awaiting_morning_standing_order", "awaiting_payment"].includes(
          String(subscription?.pendingChangeMode || subscription?.pending_change_mode || ""),
        )),
  );
  const morningStandingOrderLink =
    subscription?.morningPaymentLink || subscription?.morning_payment_link || null;

  const handleCreateMorningStandingOrder = useCallback(async () => {
    if (paymentSetupLoading) return;
    setPaymentSetupLoading(true);
    setPaymentSetupError(null);
    try {
      const targetTier = pendingPackageCode || packageCode;
      const payload = openAgentPaymentMethodSetup({ packageCode: targetTier });
      const setupUrl =
        morningStandingOrderLink || resolveAgentBillingSetupRedirectUrl(payload);
      if (!setupUrl || payload?.ok === false) {
        setPaymentSetupError("לא ניתן לפתוח את לינק Morning. נסו שוב או פנו לתמיכה.");
        setPaymentSetupLoading(false);
        return;
      }
      window.location.assign(setupUrl);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "שגיאה בפתיחת לינק Morning. נסו שוב.";
      setPaymentSetupError(String(msg));
      setPaymentSetupLoading(false);
    }
  }, [
    morningStandingOrderLink,
    packageCode,
    paymentSetupLoading,
    pendingPackageCode,
  ]);

  const handleDefinePaymentMethod = useCallback(async () => {
    if (paymentSetupLoading) return;
    setPaymentSetupLoading(true);
    setPaymentSetupError(null);
    try {
      const payload = openAgentPaymentMethodSetup({ packageCode });
      const setupUrl = resolveAgentBillingSetupRedirectUrl(payload);
      if (!setupUrl || payload?.ok === false) {
        if (payload?.error === "external_payment_link_not_configured") {
          setPaymentSetupError(
            `קישור תשלום לחבילה ${payload?.packageCode || packageCode || ""} טרם הוגדר. פנו לתמיכה.`,
          );
        } else {
          setPaymentSetupError("לא ניתן לפתוח דף שמירת אמצעי תשלום. נסו שוב.");
        }
        setPaymentSetupLoading(false);
        return;
      }
      window.location.assign(setupUrl);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "שגיאה בפתיחת דף התשלום. נסו שוב.";
      setPaymentSetupError(String(msg));
      setPaymentSetupLoading(false);
    }
  }, [packageCode, paymentSetupLoading]);

  const resolvedPackageFeatures = resolvedPackage?.features || {};
  const isPro = packageCode === "PRO";
  const isAgency = packageCode === "AGENCY";
  const extraSeatsNet = Number(billing?.extra_seats_net) || 0;
  const extraSeatsCount = Number(seatBilling?.extraSeats) || 0;
  const hasExtraSeats = extraSeatsNet > 0 || extraSeatsCount > 0;
  /** זיכוי צוות = מודל PRO (offset). סוכנות מחויבת לפי מושבים, לא לפי זיכוי. */
  const showTeamCreditLine = isPro;
  const billingAccess = Boolean(resolvedPackageFeatures?.billing_access);
  /** PRO: תצוגת offset בזמן אמת. סוכנות: רק כשיש מושבים נוספים שמשנים את הסכום. */
  const canShowBillingExpectedCard = billingAccess && (isPro || hasExtraSeats);
  /** BASIC נשאר עם כרטיס נעול (שדרוג). סוכנות בלי מושבים נוספים — בלי כרטיס כפול. */
  const showExpectedChargeColumn =
    isPro || hasExtraSeats || (!billingAccess && !isAgency);
  const effectiveStatus = subscription?.status;
  const subStatusLc = String(effectiveStatus || "").trim().toLowerCase();

  const subscriptionBillingSummary = useMemo(
    () =>
      resolveSubscriptionBillingSummary({
        packageCode,
        monthlyGrossAmount:
          displayBilling?.monthly_gross_amount ??
          billingStatus?.monthlyGrossAmount ??
          resolvePackageSubscriptionGrossAmount(currentPackageNetAmount),
        nextBillingDateDisplay: displayBilling?.next_billing_date_display,
        nextChargeAt: scheduledNextIso ?? billingStatus?.nextChargeAt,
        standingOrderStatus: displayBilling?.standing_order_status,
        standingOrderStatusHe: displayBilling?.standing_order_status_he,
        subscriptionBilling:
          displayBilling?.subscription_billing ??
          subscription?.subscription_billing ??
          billingStatus?.subscriptionBilling,
        displayBilling,
      }),
    [
      billingStatus?.monthlyGrossAmount,
      billingStatus?.nextChargeAt,
      billingStatus?.subscriptionBilling,
      currentPackageNetAmount,
      displayBilling,
      packageCode,
      scheduledNextIso,
      subscription?.subscription_billing,
    ],
  );

  /** Account overview API failed; rely on global billing context only — do not infer package/pricing from partial data. */
  const overviewLoadFailed = Boolean(error && !data && !loading);

  // `showBillingMigrationGate` מגיע מה-context המשותף —
  // כך הבאנר הגלובלי ב-AgentDashboardChrome והחסימה של Upgrade CTA כאן
  // נשענים על אותו מקור אמת, בלי כפילות קריאות או תצוגה.
  const showUpgradeCta = overviewLoadFailed
    ? false
    : shouldShowUpgradeCta({
        resolvedPackageCode: packageCode,
        subscriptionStatus: subStatusLc,
        showBillingMigrationGate,
      });
  const showTeamCard = Boolean(team?.is_team_lead);

  const showTeamTableResolved = useMemo(() => {
    if (!team?.is_team_lead) return false;
    if (canUseTeamManagement) return true;
    return Array.isArray(team.members) && team.members.length > 0;
  }, [team, canUseTeamManagement]);

  const overviewCardCount =
    2 + (showExpectedChargeColumn ? 1 : 0) + (showTeamCard ? 1 : 0);
  const cardCol =
    overviewCardCount >= 4
      ? { xs: 24, sm: 12, lg: 6 }
      : overviewCardCount === 2
        ? { xs: 24, sm: 12, lg: 12 }
        : { xs: 24, sm: 12, lg: 8 };
  const packagePlan = getRegistrationPlanDefinition(packageCode);
  const packageChargeDisplay =
    resolvePackageSubscriptionChargeDisplay(currentPackageNetAmount);
  const packageHighlights = packageCardHighlights(packageCode, seatBilling);

  const members = Array.isArray(team?.members) ? team.members : [];

  const salesAuditFilters = useMemo(() => {
    const q = String(salesAuditQuery || "").trim().toLowerCase();
    const byQuery = (row) => {
      if (!q) return true;
      const haystack = [
        row?.order_id,
        row?.display_ref,
        row?.customer_name,
        row?.product_type,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    };
    return {
      included: (salesAuditData?.included_orders || []).filter(byQuery),
      excluded: (salesAuditData?.excluded_orders || []).filter(byQuery),
    };
  }, [salesAuditData, salesAuditQuery]);

  const openSalesAudit = useCallback(async () => {
    setSalesAuditOpen(true);
    setSalesAuditLoading(true);
    setSalesAuditError(null);
    try {
      const payload = await getAgentBillingExemptionSalesAudit(auditMonth);
      setSalesAuditData(payload || null);
    } catch (e) {
      setSalesAuditError(e?.response?.data?.message || e?.message || "טעינה נכשלה");
      setSalesAuditData(null);
    } finally {
      setSalesAuditLoading(false);
    }
  }, [auditMonth]);

  const getSalesAuditOrderDashboardUrl = useCallback((row) => {
    const fromApi = String(row?.detail_path || "").trim();
    if (fromApi.startsWith("/p/dashboard/orders/")) return fromApi;

    const orderId = String(row?.order_id || "").trim();
    if (!orderId) return null;
    const source = String(row?.source || "").trim().toLowerCase();
    const family = String(
      row?.resolved_product_family || row?.product_type || ""
    ).trim().toLowerCase();

    let slug = "packages";
    let orderType = "package";

    if (source === "orders") {
      slug = "hotels";
      orderType = "hotel";
    } else if (source === "dynamic_packages") {
      slug = "dynamic-packages";
      orderType = "dynamic";
    } else if (source === "lds_orders") {
      if (family === "flight" || family === "flights") {
        slug = "flights";
        orderType = "flight";
      } else if (family === "organized_tour") {
        slug = "organized-tours";
        orderType = "organized_tour";
      } else {
        slug = "packages";
        orderType = "package";
      }
    }

    const qs = new URLSearchParams({
      orderId,
      orderType,
    }).toString();
    return `${ordersDashboardCategoryPath(slug)}?${qs}`;
  }, []);

  const openSalesAuditOrder = useCallback(
    (row) => {
      const target = getSalesAuditOrderDashboardUrl(row);
      if (!target) return;
      setSalesAuditOpen(false);
      navigate(target);
    },
    [getSalesAuditOrderDashboardUrl, navigate]
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const p = billingExemptionPayload || {};
    console.log("[AGENT_BILLING_EXEMPTION_UI_TRACE]", {
      widget: "account_overview_card",
      endpoint: billingExemptionSource,
      sales_month: p.sales_month ?? null,
      included_total_ils: p.monthly_sales_current_month_ils ?? null,
      threshold: p.monthly_sales_threshold_ils ?? null,
      progress_percent: p.monthly_sales_progress_percent ?? null,
      calculation_mode: p.calculation_mode ?? null,
      calculation_version: p.calculation_version ?? null,
      qualification_hash: p.qualification_hash ?? null,
    });
  }, [billingExemptionPayload, billingExemptionSource]);

  useEffect(() => {
    if (!import.meta.env.DEV || !salesAuditOpen) return;
    const p = salesAuditData || {};
    console.log("[AGENT_BILLING_EXEMPTION_UI_TRACE]", {
      widget: "sales_audit_drawer",
      endpoint: "/api/agent/billing-exemption/sales-audit",
      sales_month: p.sales_month ?? auditMonth ?? null,
      included_total_ils: p.included_total_ils ?? null,
      threshold: p.monthly_sales_threshold_ils ?? billingExemptionPayload?.monthly_sales_threshold_ils ?? null,
      progress_percent:
        p.monthly_sales_progress_percent ?? billingExemptionPayload?.monthly_sales_progress_percent ?? null,
      calculation_mode: p.calculation_mode ?? null,
      calculation_version: p.calculation_version ?? null,
      qualification_hash: p.qualification_hash ?? billingExemptionPayload?.qualification_hash ?? null,
    });
  }, [auditMonth, billingExemptionPayload?.monthly_sales_progress_percent, billingExemptionPayload?.monthly_sales_threshold_ils, billingExemptionPayload?.qualification_hash, salesAuditData, salesAuditOpen]);

  const tableColumns = useMemo(
    () => [
      { title: "שם סוכן", dataIndex: "full_name", key: "full_name" },
      { title: "קוד סוכן", dataIndex: "agent_code", key: "agent_code", width: 110 },
      {
        title: "סטטוס",
        dataIndex: "status",
        key: "status",
        render: (s, row) =>
          row?.is_team_seat ? <Tag color="blue">מושב צוות</Tag> : subscriptionStatusTagHe(s),
      },
      {
        title: "חבילה",
        dataIndex: "package_code",
        key: "package_code",
        render: (c) => {
          const n = teamMemberPackageCodeForDisplay(c, {
            leadPackageCode: packageCode,
            isAgencyTeam: isAgencyTeamDisplay,
          });
          const lbl = agentPackageLabelHe(n);
          if (n === "AGENCY") return <Tag color="blue">{lbl}</Tag>;
          if (n === "PRO") return <Tag color="gold">{lbl}</Tag>;
          return <Tag>{lbl}</Tag>;
        },
      },
      {
        title: "חלוקת דמי טיפול",
        key: "handling_fee_split",
        width: 280,
        render: (_, row) => {
          const fallback = Number(defaultSplitPercent || 100);
          const current =
            row?.handling_fee_agent_percent != null
              ? Number(row.handling_fee_agent_percent)
              : fallback;
          return (
            <HandlingFeeSplitSlider
              size="small"
              value={current}
              disabled={memberSplitSavingId === row.id}
              onChange={(v) => {
                setData((prev) => {
                  if (!prev) return prev;
                  const list = Array.isArray(prev.team?.members) ? prev.team.members : [];
                  return {
                    ...prev,
                    team: {
                      ...(prev.team || {}),
                      members: list.map((m) =>
                        m.id === row.id ? { ...m, handling_fee_agent_percent: v } : m
                      ),
                    },
                  };
                });
              }}
              onChangeComplete={(v) => persistMemberSplit(row.id, v)}
            />
          );
        },
      },
    ],
    [defaultSplitPercent, isAgencyTeamDisplay, memberSplitSavingId, packageCode, persistMemberSplit],
  );

  const auditIncludedColumns = useMemo(
    () => [
      { title: "מקור", dataIndex: "source", key: "source", width: 100 },
      { title: "מספר הזמנה", dataIndex: "order_id", key: "order_id", width: 120 },
      { title: "אסמכתא", dataIndex: "display_ref", key: "display_ref", width: 120 },
      { title: "לקוח", dataIndex: "customer_name", key: "customer_name" },
      { title: "מוצר", dataIndex: "product_type", key: "product_type", width: 120 },
      {
        title: "סכום",
        dataIndex: "amount_ils",
        key: "amount_ils",
        width: 120,
        render: (v) => formatMoney(v, "ILS"),
      },
      { title: "סטטוס", dataIndex: "status", key: "status", width: 120 },
      { title: "מקור ספירה", dataIndex: "included_source", key: "included_source", width: 120 },
      {
        title: "מעבר",
        key: "navigate",
        width: 120,
        render: (_, row) => (
          <Button type="link" size="small" onClick={() => openSalesAuditOrder(row)}>
            פתח הזמנה
          </Button>
        ),
      },
    ],
    [openSalesAuditOrder]
  );

  const excludedReasonHe = useCallback((reason) => {
    const r = String(reason || "").trim().toLowerCase();
    const map = {
      pending_payment: "ממתין לתשלום",
      cancelled: "מבוטל",
      draft: "טיוטה",
      duplicate: "נספר כבר במקור אחר",
      invalid_amount: "סכום לא תקין",
      unsupported_status: "סטטוס לא מזכה",
    };
    return map[r] || "לא נכלל בחישוב";
  }, []);

  const auditExcludedColumns = useMemo(
    () => [
      { title: "מקור", dataIndex: "source", key: "source", width: 100 },
      { title: "מספר הזמנה", dataIndex: "order_id", key: "order_id", width: 120 },
      { title: "אסמכתא", dataIndex: "display_ref", key: "display_ref", width: 120 },
      { title: "לקוח", dataIndex: "customer_name", key: "customer_name" },
      { title: "מוצר", dataIndex: "product_type", key: "product_type", width: 120 },
      {
        title: "סכום",
        dataIndex: "amount_ils",
        key: "amount_ils",
        width: 120,
        render: (v) => formatMoney(v, "ILS"),
      },
      { title: "סטטוס", dataIndex: "status", key: "status", width: 120 },
      {
        title: "סיבת אי הכללה",
        dataIndex: "excluded_reason",
        key: "excluded_reason",
        width: 180,
        render: (v) => excludedReasonHe(v),
      },
      {
        title: "מעבר",
        key: "navigate",
        width: 120,
        render: (_, row) => (
          <Button type="link" size="small" onClick={() => openSalesAuditOrder(row)}>
            פתח הזמנה
          </Button>
        ),
      },
    ],
    [excludedReasonHe, openSalesAuditOrder]
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[BILLING_WIDGET_GUARD]", {
      packageCode,
      canViewBilling: canShowBillingExpectedCard,
    });
  }, [canShowBillingExpectedCard, packageCode]);

  useEffect(() => {
    if (!import.meta.env.DEV || !data) return;
    console.log("[BILLING_CARD_DEBUG]", {
      agentId: data?.agent?.id,
      agentName: data?.agent?.full_name,
      packageCode,
      billingStatusRaw: billingStatus ?? null,
      subscriptionRaw: subscription ?? null,
      displayBillingRaw: displayBilling ?? null,
      billingBlockRaw: data?.billing ?? null,
      hasPaymentMethod: displayBilling?.has_payment_method,
      currentChargeResolved: currentPackageChargeAmount,
      dueTodayDisplayed: billing?.current_charge_amount,
      canShowBillingExpectedCard,
    });
  }, [
    billing?.current_charge_amount,
    billingStatus,
    canShowBillingExpectedCard,
    currentPackageChargeAmount,
    data,
    displayBilling,
    packageCode,
    subscription,
  ]);

  useEffect(() => {
    // TODO: remove [AGENT_PACKAGE_SYNC_TRACE] after package source sync validation is complete.
    console.debug("[AGENT_PACKAGE_SYNC_TRACE]", {
      screen: "account_overview",
      agent_package: data?.agent?.package,
      agent_plan: data?.agent?.plan,
      agent_package_id: data?.agent?.package_id,
      resolved_package_code: data?.resolvedPackage?.code,
      billing_package_code: billingStatus?.packageCode,
      subscription_package_code: subscription?.package_code,
      subscription_package_id: subscription?.package_id,
      resolved_displayed_package: resolvedDisplayedPackage.packageCode,
      resolved_source: resolvedDisplayedPackage.source,
    });
  }, [
    billingStatus?.packageCode,
    data?.agent?.package,
    data?.agent?.package_id,
    data?.agent?.plan,
    data?.resolvedPackage?.code,
    resolvedDisplayedPackage.packageCode,
    resolvedDisplayedPackage.source,
    subscription?.package_code,
    subscription?.package_id,
  ]);

  if (loading && !data) {
    return (
      <div className="agent-account-overview agent-account-overview--loading">
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: "40%" }} />
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4].map((k) => (
            <Col key={k} xs={24} sm={12} lg={6}>
              <Card>
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div className="agent-account-overview">
      {error && (
        <Alert
          type="warning"
          showIcon
          message="לא ניתן לטעון את סיכום החשבון"
          description={error}
          action={
            <Button size="small" onClick={load}>
              נסה שוב
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {data?.agent?.full_name ? (
        <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
          <Text type="secondary">שלום,</Text>
          <Title level={4} style={{ margin: 0 }}>
            {data.agent.full_name}
          </Title>
        </Space>
      ) : null}

      {/* באנר Billing Migration מוצג גלובלית דרך AdminMessageBanner
          (מאוחד עם admin broadcasts בסדר עדיפויות). כאן רק מסתמכים על אותו
          showBillingMigrationGate מה-context כדי לחסום את ה-Upgrade CTA. */}

      <Row gutter={[16, 16]} className="agent-account-overview__cards">
        <Col {...cardCol}>
          <Card
            className={`agent-account-overview__pkg-card${
              isPro
                ? " agent-account-overview__pkg-card--pro"
                : isAgency
                  ? " agent-account-overview__pkg-card--agency"
                  : ""
            }`}
            title={
              <Space>
                {isPro ? <CrownOutlined /> : isAgency ? <BankOutlined /> : null}
                <span>החבילה שלי</span>
              </Space>
            }
          >
            <div className="agent-account-overview__pkg-card-body">
              <div>
                <div className="agent-account-overview__package-code">
                  {overviewLoadFailed ? (
                    <Text type="secondary">לא ניתן לטעון פרטי חבילה כרגע</Text>
                  ) : (
                    packageNameHe ||
                    resolvedPackage?.name ||
                    resolvedDisplayedPackage.packageNameHe
                  )}
                </div>
                <div style={{ marginTop: 8 }}>
                  {overviewLoadFailed ? <Tag>—</Tag> : subscriptionStatusTagHe(effectiveStatus)}
                </div>
                {!overviewLoadFailed && packagePlan?.subtitle ? (
                  <Text type="secondary" className="agent-account-overview__pkg-subtitle">
                    {packagePlan.subtitle}
                  </Text>
                ) : null}
                {!overviewLoadFailed && packageChargeDisplay.grossFormatted !== "—" ? (
                  <div className="agent-account-overview__pkg-price">
                    <Text strong>{packageChargeDisplay.grossFormatted}</Text>
                    <Text type="secondary"> {packageChargeDisplay.vatIncludedLabel} לחודש</Text>
                  </div>
                ) : null}
                {!overviewLoadFailed ? (
                  <ul className="agent-account-overview__pkg-highlights">
                    {packageHighlights.map((line) => (
                      <li key={line}>
                        <CheckCircleOutlined aria-hidden />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!overviewLoadFailed && requiresNewStandingOrder && pendingPackageCode ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12, textAlign: "right" }}
                    message={`שינוי חבילה ממתין: ${pendingPackageCode}`}
                    description="נדרש להקים הוראת קבע חדשה. החבילה תעודכן רק לאחר אישור Admin."
                  />
                ) : null}
                {!overviewLoadFailed && requiresNewStandingOrder ? (
                  <Button
                    type="primary"
                    size="small"
                    style={{ marginTop: 12 }}
                    loading={paymentSetupLoading}
                    disabled={paymentSetupLoading}
                    onClick={handleCreateMorningStandingOrder}
                  >
                    הקם הוראת קבע חדשה
                  </Button>
                ) : null}
                {showUpgradeCta && !requiresNewStandingOrder ? (
                  <Button
                    type="primary"
                    size="small"
                    style={{ marginTop: 12 }}
                    onClick={() =>
                      navigateAgentPackageUpgrade(navigate, "account_overview_upgrade_pro")
                    }
                  >
                    שדרג ל־PRO
                  </Button>
                ) : null}
              </div>
              {!overviewLoadFailed && packagePlan?.noCommitment ? (
                <Text type="secondary" className="agent-account-overview__pkg-foot">
                  {packagePlan.noCommitment}
                </Text>
              ) : null}
            </div>
          </Card>
        </Col>

        <Col {...cardCol}>
          <Card title="חיוב נוכחי">
            {overviewLoadFailed ? (
              <Text type="secondary">לא ניתן לטעון נתוני חיוב כרגע</Text>
            ) : (
              <>
                <SubscriptionBillingSummaryBlock
                  summary={subscriptionBillingSummary}
                  titleLevel={3}
                />
                {!overviewLoadFailed && requiresNewStandingOrder ? (
                  <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 8 }}>
                    <Text type="warning" style={{ fontSize: 12, display: "block" }}>
                      נדרש להקים הוראת קבע חדשה עבור החבילה החדשה
                      {pendingPackageCode ? ` (${pendingPackageCode})` : ""}
                    </Text>
                    {paymentSetupError ? (
                      <Alert type="error" showIcon message={paymentSetupError} />
                    ) : null}
                    <Button
                      type="primary"
                      size="small"
                      loading={paymentSetupLoading}
                      disabled={paymentSetupLoading}
                      onClick={handleCreateMorningStandingOrder}
                    >
                      הקם הוראת קבע חדשה
                    </Button>
                  </Space>
                ) : hasPaymentMethod ? (
                  <Text type="success" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                    אמצעי תשלום מוגדר
                  </Text>
                ) : displayBilling?.has_payment_method === false ||
                  billingStatus?.hasActivePaymentCoverage === false ? (
                  <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 8 }}>
                    <Text type="warning" style={{ fontSize: 12, display: "block" }}>
                      לא מוגדר אמצעי תשלום
                    </Text>
                    {paymentSetupError ? (
                      <Alert type="error" showIcon message={paymentSetupError} />
                    ) : null}
                    <Button
                      type="primary"
                      size="small"
                      loading={paymentSetupLoading}
                      disabled={paymentSetupLoading}
                      onClick={handleDefinePaymentMethod}
                    >
                      הגדר אמצעי תשלום
                    </Button>
                  </Space>
                ) : null}
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 10 }}>
                  חיוב חודשי ב-1 לחודש · Morning מחייב אוטומטית לאחר אישור הוראת קבע
                </Text>
              </>
            )}
          </Card>
        </Col>

        {showExpectedChargeColumn ? (
        <Col {...cardCol}>
          {overviewLoadFailed ? (
            <Card title="חיוב צפוי להיום">
              <Text type="secondary">לא ניתן לטעון חיוב צפוי כרגע</Text>
            </Card>
          ) : (
            <FeatureGuardCard
              isEnabled={canShowBillingExpectedCard}
            title="חיוב צפוי להיום"
            lockedTooltip="גישה לנתונים פיננסיים זמינה בחבילת PRO ומעלה"
            lockedBodyText="פיצ'ר זמין בחבילת PRO"
            upgradeButtonText="שדרג ל-PRO"
            upgradeSource="account_overview_billing_expected"
          >
            <SubscriptionChargeAmountBlock
              netAmount={billing?.current_charge_amount}
              titleLevel={2}
            />
            <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
              <Text type="secondary">
                סכום בסיס:{" "}
                <Text type="secondary" strong>
                  {formatMoney(billing?.base_amount, billCurrency)}
                </Text>
              </Text>
              {showTeamCreditLine ? (
                <Text type="secondary">
                  זיכוי צוות:{" "}
                  <Text type="secondary" strong>
                    {formatMoney(billing?.team_credit, billCurrency)}
                  </Text>
                </Text>
              ) : null}
              {billing?.extra_seats_net ? (
                <Text type="secondary">
                  מושבים נוספים (נטו):{" "}
                  <Text type="secondary" strong>
                    {formatMoney(billing.extra_seats_net, billCurrency)}
                  </Text>
                </Text>
              ) : null}
            </Space>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 12 }}>
              {subscriptionBillingSummary?.nextBillingDateDisplay
                ? `מועד חיוב חודשי: ${subscriptionBillingSummary.nextBillingDateDisplay}`
                : scheduledNextIso
                  ? `מועד חיוב חודשי: ${formatSubscriptionInstant(scheduledNextIso)}`
                  : "אין חיוב עתידי מוגדר"}
            </Text>
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
              הסכום מתעדכן בזמן אמת והחיוב הסופי יתבצע במועד החיוב החודשי
            </Text>
          </FeatureGuardCard>
          )}
        </Col>
        ) : null}

        {showTeamCard ? (
          <Col {...cardCol}>
            <Card
              title={
                <Space>
                  <TeamOutlined />
                  <span>הצוות שלי</span>
                </Space>
              }
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="חברי צוות" value={team?.members_count ?? 0} />
                </Col>
                <Col span={12}>
                  <Statistic title="פעילים בתשלום" value={team?.active_members_count ?? 0} />
                </Col>
              </Row>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
                  שם הסוכנות (יוצג בדשבורד של כל סוכני הצוות)
                </Text>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    value={agencyNameDraft}
                    maxLength={120}
                    placeholder="לדוגמה: סוכנות נסיעות אלול"
                    onChange={(e) => setAgencyNameDraft(e.target.value)}
                  />
                  <Button type="primary" loading={agencyNameSaving} onClick={saveAgencyName}>
                    שמור
                  </Button>
                </Space.Compact>
              </div>
              {seatBilling ? (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                    מושבים בחבילה
                  </Text>
                  <Text>
                    {seatBilling.totalUsers} / {seatBilling.includedUsers} כלולים
                    {seatBilling.extraSeats > 0
                      ? ` · ${seatBilling.extraSeats} נוספים ב־${formatMoney(
                          seatBilling.extraSeatsGross,
                          "ILS"
                        )} לחודש (120 ₪ + מע״מ ליחידה)`
                      : " · היוזר הבא יישאר במסגרת החבילה"}
                  </Text>
                </div>
              ) : null}
              {canUseTeamManagement ? (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  style={{ marginTop: 16 }}
                  onClick={() => setCreateUserOpen(true)}
                  block
                >
                  הקם יוזר חדש
                </Button>
              ) : null}
            </Card>
          </Col>
        ) : null}
      </Row>

      <ReferralInviteCard packageCode={packageCode} agent={data?.agent} />

      {!showTeamCard && teamAgencyName ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message={`סוכנות: ${teamAgencyName}`}
          description="שם הסוכנות מוגדר על ידי מוביל הצוות ומוצג גם בדשבורד."
        />
      ) : null}

      <div className="agent-account-overview__billing-exemption-block">
        <BillingExemptionCard
          title="התקדמות מכירות חודשית"
          payload={billingExemptionPayload}
          source={billingExemptionSource}
          loading={loading && !data && !error}
        />
        <div className="agent-account-overview__billing-exemption-actions">
          <Button type="link" onClick={openSalesAudit}>
            הצג פירוט הזמנות שנכללו
          </Button>
        </div>
      </div>

      {showTeamTableResolved ? (
        <Card
          title="רשימת צוות"
          style={{ marginTop: 16 }}
          className="agent-account-overview__team-table"
          extra={
            canUseTeamManagement ? (
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => setCreateUserOpen(true)}>
                הקם יוזר
              </Button>
            ) : null
          }
        >
          {canUseTeamManagement ? (
            <div className="agent-account-overview__split-block">
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                חלוקת דמי טיפול — ברירת מחדל לצוות
              </Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                גררו ימינה כדי להשאיר יותר לסוכן, ושמאלה כדי להעביר יותר למוביל הצוות. לדוגמה 85% לסוכן ו־15% אליכם.
              </Text>
              <HandlingFeeSplitSlider
                value={defaultSplitPercent}
                disabled={defaultSplitSaving}
                onChange={(v) => {
                  setData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      team: {
                        ...(prev.team || {}),
                        handling_fee_split: {
                          agent_percent: v,
                          team_lead_percent: Math.round((100 - v) * 100) / 100,
                        },
                      },
                    };
                  });
                }}
                onChangeComplete={(v) => persistDefaultSplit(v)}
              />
            </div>
          ) : null}
          {seatBilling?.extraSeats > 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${seatBilling.extraSeats} מושבים מעבר ל־4 הכלולים בחבילה`}
              description={`חיוב נוסף ${formatMoney(seatBilling.extraSeatsGross, "ILS")} לחודש (120 ₪ + מע״מ לכל יוזר מהחמישי ואילך).`}
            />
          ) : null}
          <Table
            rowKey="id"
            dataSource={members}
            columns={tableColumns}
            pagination={members.length > 8 ? { pageSize: 8 } : false}
            locale={{ emptyText: "אין חברי צוות" }}
            size="middle"
          />
        </Card>
      ) : null}

      <Drawer
        title="פירוט חישוב התקדמות מכירות חודשית"
        open={salesAuditOpen}
        placement="left"
        width={screens.md ? 980 : "100%"}
        onClose={() => setSalesAuditOpen(false)}
        destroyOnClose={false}
      >
        {salesAuditError ? (
          <Alert type="warning" showIcon message="לא ניתן לטעון פירוט" description={salesAuditError} />
        ) : null}
        <Space direction="vertical" size={12} style={{ display: "flex" }}>
          <Card size="small">
            <Space direction="vertical" size={4}>
              <Text strong>חודש חישוב: {salesAuditData?.sales_month || auditMonth}</Text>
              <Text>
                סה״כ שנכלל: {formatMoney(salesAuditData?.included_total_ils, "ILS")} | מספר הזמנות:
                {" "}
                {salesAuditData?.included_orders_count ?? 0}
              </Text>
            </Space>
          </Card>
          <Input
            allowClear
            placeholder="חיפוש לפי לקוח / מספר הזמנה / אסמכתא"
            value={salesAuditQuery}
            onChange={(e) => setSalesAuditQuery(e.target.value)}
          />
          <Tabs
            items={[
              {
                key: "included",
                label: `נכללו (${salesAuditData?.included_orders_count ?? 0})`,
                children: salesAuditLoading ? (
                  <Skeleton active paragraph={{ rows: 5 }} />
                ) : salesAuditFilters.included.length === 0 ? (
                  <Empty description="אין הזמנות שנכללו" />
                ) : screens.md ? (
                  <Table
                    rowKey={(r) => `${r.source}-${r.order_id}`}
                    dataSource={salesAuditFilters.included}
                    columns={auditIncludedColumns}
                    pagination={{ pageSize: 8 }}
                    size="small"
                  />
                ) : (
                  <List
                    dataSource={salesAuditFilters.included}
                    renderItem={(item) => (
                      <Card size="small" style={{ marginBottom: 8 }}>
                        <Space direction="vertical" size={2}>
                          <Text strong>{item.display_ref || item.order_id}</Text>
                          <Text type="secondary">{item.customer_name || "—"}</Text>
                          <Text>{formatMoney(item.amount_ils, "ILS")}</Text>
                          <Button type="link" size="small" onClick={() => openSalesAuditOrder(item)}>
                            פתח הזמנה
                          </Button>
                        </Space>
                      </Card>
                    )}
                  />
                ),
              },
              {
                key: "excluded",
                label: `לא נכללו (${salesAuditData?.excluded_orders_count ?? 0})`,
                children: salesAuditLoading ? (
                  <Skeleton active paragraph={{ rows: 5 }} />
                ) : salesAuditFilters.excluded.length === 0 ? (
                  <Empty description="אין הזמנות שלא נכללו" />
                ) : screens.md ? (
                  <Table
                    rowKey={(r) => `${r.source}-${r.order_id}`}
                    dataSource={salesAuditFilters.excluded}
                    columns={auditExcludedColumns}
                    pagination={{ pageSize: 8 }}
                    size="small"
                  />
                ) : (
                  <List
                    dataSource={salesAuditFilters.excluded}
                    renderItem={(item) => (
                      <Card size="small" style={{ marginBottom: 8 }}>
                        <Space direction="vertical" size={2}>
                          <Text strong>{item.display_ref || item.order_id}</Text>
                          <Text type="secondary">{excludedReasonHe(item.excluded_reason)}</Text>
                          <Text>{formatMoney(item.amount_ils, "ILS")}</Text>
                          <Button type="link" size="small" onClick={() => openSalesAuditOrder(item)}>
                            פתח הזמנה
                          </Button>
                        </Space>
                      </Card>
                    )}
                  />
                ),
              },
            ]}
          />
        </Space>
      </Drawer>

      <Modal
        title="הקמת יוזר לצוות"
        open={createUserOpen}
        onCancel={() => {
          if (!createUserSubmitting) setCreateUserOpen(false);
        }}
        onOk={submitCreateUser}
        okText="צור משתמש"
        cancelText="ביטול"
        confirmLoading={createUserSubmitting}
        destroyOnClose
      >
        {seatBilling?.nextSeatIsBillable ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="היוזר הזה הוא החמישי או יותר"
            description="ייגבה 120 ₪ + מע״מ לחודש עבור המושב הנוסף, בנוסף למנוי הסוכנות."
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="כלול בחבילה"
            description="עד 4 משתמשים כלולים במנוי הסוכנות. מהיוזר החמישי — 120 ₪ + מע״מ לחודש."
          />
        )}
        <Form form={createUserForm} layout="vertical">
          <Form.Item
            name="firstName"
            label="שם פרטי"
            rules={[{ required: true, message: "יש להזין שם פרטי" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="שם משפחה"
            rules={[{ required: true, message: "יש להזין שם משפחה" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="email"
            label="אימייל"
            rules={[
              { required: true, message: "יש להזין אימייל" },
              { type: "email", message: "כתובת אימייל אינה תקינה" },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="phone" label="טלפון">
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label="סיסמה ראשונית"
            rules={[{ required: true, min: 8, message: "לפחות 8 תווים" }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
